import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { colors } from '../../tokens/colors'
import { typography } from '../../tokens/typography'

/** *** Common components **** */
export const Tooltip = styled.div`
  display: none;
  position: absolute;
  bottom: 150%;
  left: 50%;
  padding: 0 4px;
  border-radius: 4px;
  opacity: 0.9;
  transform: translateX(-50%);
  transition: opacity 0.2s ease-in-out;
  ${typography.down1.regularSans}
  color: ${colors.feedback.infoLight};
  background: ${colors.feedback.infoDark};
  box-shadow:
    0 0 0 1px ${colors.feedback.infoDark} inset,
    0 0 0 2px ${colors.interactive.shadowActiveDarkBlue};

  &::before {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 5px 5px 0;
    border-style: solid;
    border-color: ${colors.feedback.infoDark} transparent transparent;
  }
`

export const Spinner = styled.div<{ borderSize?: number; color?: string }>`
  ${({ borderSize, color }) => css`
    border: ${borderSize || 1}px solid ${color || colors.copy.paragraphDark};
    border-top: 1px solid transparent;
    border-radius: 50%;
    width: 14px;
    height: 14px;
    margin: 3px;
    animation: spin 1s linear infinite;

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `}
`

const transformEaseIn = css`
  transition: transform 100ms ease-in;
`

const popOutAnimation = css`
  transform: translate(3px, -3px);
`

export const Spacer = styled.div<{
  hasLeadingElement: boolean
  hasTrailingElement: boolean
}>`
  ${({ hasLeadingElement, hasTrailingElement }) => css`
    ${hasLeadingElement && 'margin-left: 4px;'}
    ${hasTrailingElement && 'margin-right: 4px;'}
  `}
`

/** *** Variant: Chip **** */

const chipButtonLoadingStyles = css`
  cursor: not-allowed;
  pointer-events: none;
  color: ${colors.copy.paragraphDark};
  background: ${colors.interactive.secondaryHoverFocus};
  box-shadow: 0 0 0 1px ${colors.interactive.borderActiveDarkBlue} inset;

  svg {
    color: ${colors.copy.paragraphDark};
  }
`

const chipButtonStyles = css`
  display: flex;
  padding: 2px;
  align-items: center;
  background: ${colors.interactive.secondaryEnabled};
  color: ${colors.interactive.primaryEnabled};
  border: 1px solid ${colors.interactive.secondaryEnabled};

  p {
    padding: 0 4px;
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${colors.interactive.primaryEnabled};
  }

  &:hover {
    color: ${colors.interactive.primaryHoverFocus};
    background: ${colors.interactive.secondaryHoverFocus};
    box-shadow: 0 0 0 1px ${colors.interactive.borderEnabledHoverGray} inset;
  }

  &:focus {
    color: ${colors.interactive.primaryHoverFocus};
    background: ${colors.interactive.secondaryHoverFocus};
    box-shadow:
      0 0 0 3px ${colors.interactive.shadowFocusOrange},
      0 0 0 1px ${colors.interactive.borderFocusOrange} inset;
  }

  &:active {
    color: ${colors.interactive.primaryHoverFocus};
    background: ${colors.interactive.secondaryHoverFocus};
    box-shadow: 0 0 0 1px ${colors.interactive.borderActiveDarkBlue} inset;
  }

  &:disabled {
    pointer-events: none;
    color: ${colors.interactive.disabledLight};
    background: ${colors.background.primaryBackground};
    border: 1px solid ${colors.background.primaryBackground};

    svg {
      color: ${colors.interactive.disabledLight};
    }
  }
`

/** *** Variant: Inline **** */

export type InlineSize = 'large' | 'small'

const inlineButtonLoadingStyles = css`
  cursor: not-allowed;
  pointer-events: none;
  color: ${colors.copy.paragraphDark};
  background: ${colors.interactive.tableHoverFocus};
  box-shadow: 0 0 0 1px ${colors.interactive.borderActiveDarkBlue} inset;

  svg {
    color: ${colors.copy.paragraphDark};
  }
`

const inlineButtonStyles = (size: InlineSize) => css`
  display: flex;
  padding: 4px;
  align-items: center;
  border-radius: 4px;
  gap: 4px;
  background: transparent;
  color: ${colors.interactive.primaryEnabled};
  border: 1px solid transparent;

  svg {
    width: ${size === 'large' ? '24px' : '20px'};
    height: ${size === 'large' ? '24px' : '20px'};
    color: ${colors.interactive.primaryEnabled};
  }

  &:hover {
    color: ${colors.interactive.primaryHoverFocus};
    background: ${colors.interactive.tableHoverFocus};
    box-shadow: 0 0 0 1px ${colors.interactive.borderEnabledHoverGray} inset;
  }

  &:focus {
    color: ${colors.interactive.primaryHoverFocus};
    background: ${colors.interactive.tableHoverFocus};
    box-shadow:
      0 0 0 3px ${colors.interactive.shadowFocusOrange},
      0 0 0 1px ${colors.interactive.borderFocusOrange} inset;
  }

  &:active {
    color: ${colors.interactive.primaryHoverFocus};
    background: ${colors.interactive.tableHoverFocus};
    box-shadow: 0 0 0 1px ${colors.interactive.borderActiveDarkBlue} inset;
  }

  &:disabled {
    pointer-events: none;
    color: ${colors.interactive.disabledLight};
    background: ${colors.background.primaryBackground};
    border: 1px solid ${colors.background.primaryBackground};

    svg {
      color: ${colors.interactive.disabledLight};
    }
  }
`

/** *** Variant: Primary **** */

const primaryButtonLoadingStyles = css`
  background: ${colors.interactive.primaryHoverFocus};
  border: 1px solid transparent;
  box-shadow:
    -2px 2px 0 0 ${colors.interactive.borderHoverLightBlue},
    0 0 0 1px ${colors.interactive.borderActiveLightBlue} inset;
  color: ${colors.copy.paragraphLight};
  cursor: not-allowed;
  pointer-events: none;

  svg {
    color: ${colors.copy.paragraphLight};
  }
`

const primaryButtonStyles = css`
  align-items: center;
  background: ${colors.interactive.primaryEnabled};
  border: none;
  color: ${colors.copy.paragraphLight};
  display: flex;
  justify-content: space-between;
  min-height: 24px;
  padding: 7px 22px;

  ${transformEaseIn}
  svg {
    color: ${colors.copy.paragraphLight};
    height: 24px;
    width: 24px;
  }

  &:hover {
    background: ${colors.interactive.primaryHoverFocus};
    box-shadow:
      -3px 3px 0 0 ${colors.interactive.shadowHoverLightBlue},
      0 0 0 1px ${colors.interactive.borderHoverLightBlue} inset;
    color: ${colors.copy.headerLight};

    ${popOutAnimation}
  }

  &:focus {
    background: ${colors.interactive.primaryHoverFocus};
    box-shadow:
      0 0 0 3px ${colors.interactive.shadowFocusOrange},
      0 0 0 1px ${colors.interactive.borderFocusOrange} inset;
    color: ${colors.copy.headerLight};
  }

  &:active {
    background: ${colors.interactive.primaryHoverFocus};
    box-shadow:
      -2px 2px 0 0 ${colors.interactive.shadowHoverLightBlue},
      0 0 0 1px ${colors.interactive.borderActiveLightBlue} inset;
    color: ${colors.copy.headerLight};
  }

  &:disabled {
    background: ${colors.interactive.disabledDark};
    border: 1px solid ${colors.background.primaryBackground};
    color: ${colors.interactive.disabledLight};
    pointer-events: none;

    svg {
      color: ${colors.interactive.disabledLight};
    }
  }
`

/** *** Variant: Secondary **** */

const secondaryButtonLoadingStyles = css`
  background: ${colors.interactive.secondaryHoverFocus};
  border: none;
  box-shadow:
    -2px 2px 0 0 ${colors.interactive.borderHoverLightBlue},
    0 0 0 1px ${colors.interactive.borderActiveLightBlue} inset;
  color: ${colors.copy.paragraphDark};
  cursor: not-allowed;
  pointer-events: none;

  svg {
    color: ${colors.copy.paragraphDark};
  }
`

const secondaryButtonStyles = css`
  align-items: center;
  background: ${colors.interactive.secondaryEnabled};
  border: none;
  color: ${colors.interactive.primaryEnabled};
  display: flex;
  justify-content: space-between;
  min-height: 24px;
  padding: 7px 22px;

  ${transformEaseIn}
  svg {
    color: ${colors.interactive.primaryEnabled};
    height: 24px;
    width: 24px;
  }

  &:hover {
    background: ${colors.interactive.secondaryHoverFocus};
    box-shadow:
      -3px 3px 0 0 ${colors.interactive.shadowHoverLightBlue},
      0 0 0 1px ${colors.interactive.borderHoverLightBlue} inset;
    color: ${colors.interactive.primaryHoverFocus};

    ${popOutAnimation}
  }

  &:focus {
    background: ${colors.interactive.secondaryHoverFocus};
    box-shadow:
      0 0 0 3px ${colors.interactive.shadowFocusOrange},
      0 0 0 1px ${colors.interactive.borderFocusOrange} inset;
    color: ${colors.interactive.primaryHoverFocus};
  }

  &:active {
    background: ${colors.interactive.secondaryHoverFocus};
    box-shadow:
      -2px 2px 0 0 ${colors.interactive.borderHoverLightBlue},
      0 0 0 1px ${colors.interactive.borderActiveLightBlue} inset;
    color: ${colors.interactive.primaryHoverFocus};
  }

  &:disabled {
    background: ${colors.interactive.disabledLight};
    border: 1px solid ${colors.background.primaryBackground};
    color: ${colors.interactive.disabledDark};
    pointer-events: none;

    svg {
      color: ${colors.interactive.disabledDark};
    }
  }
`

/** *** Variant: Tertiary **** */

const tertiaryButtonLoadingStyles = css`
  background: ${colors.interactive.tertiaryHoverFocus};
  border: none;
  box-shadow:
    -2px 2px 0 0 ${colors.interactive.borderHoverLightBlue},
    0 0 0 1px ${colors.interactive.borderActiveLightBlue} inset;
  color: ${colors.copy.paragraphDark};
  cursor: not-allowed;
  pointer-events: none;

  svg {
    color: ${colors.copy.paragraphDark};
  }
`

const tertiaryButtonStyles = css`
  align-items: center;
  background: ${colors.background.primaryBackground};
  border: none;
  color: ${colors.interactive.primaryEnabled};
  display: flex;
  justify-content: space-between;
  min-height: 24px;
  padding: 7px 22px;

  ${transformEaseIn}
  svg {
    color: ${colors.interactive.primaryEnabled};
    height: 24px;
    width: 24px;
  }

  &:hover {
    background: ${colors.background.secondaryBackground};
    box-shadow:
      -3px 3px 0 0 ${colors.interactive.shadowHoverLightBlue},
      0 0 0 1px ${colors.interactive.borderHoverLightBlue} inset;
    color: ${colors.interactive.primaryHoverFocus};

    ${popOutAnimation}
  }

  &:focus {
    background: ${colors.background.secondaryBackground};
    box-shadow:
      0 0 0 3px ${colors.interactive.shadowFocusOrange},
      0 0 0 1px ${colors.interactive.borderFocusOrange} inset;
    color: ${colors.interactive.primaryHoverFocus};
  }

  &:active {
    background: ${colors.background.secondaryBackground};
    box-shadow:
      -2px 2px 0 0 ${colors.interactive.borderHoverLightBlue},
      0 0 0 1px ${colors.interactive.borderActiveLightBlue} inset;
    color: ${colors.interactive.primaryHoverFocus};
  }

  &:disabled {
    background: ${colors.interactive.disabledLight};
    border: 1px solid ${colors.background.primaryBackground};
    color: ${colors.interactive.disabledDark};
    pointer-events: none;

    svg {
      color: ${colors.interactive.disabledDark};
    }
  }
`

/** *** Variant: Form **** */

const formButtonLoadingStyles = css`
  background: ${colors.interactive.tertiaryHoverFocus};
  box-shadow: 0 0 0 1px ${colors.interactive.primaryHoverFocus} inset;
  color: ${colors.copy.paragraphDark};
  cursor: not-allowed;
  pointer-events: none;

  svg {
    color: ${colors.copy.paragraphDark};
  }
`

const formButtonStyles = css`
  align-items: center;
  background: ${colors.background.primaryBackground};
  border: 1px solid ${colors.background.primaryBackground};
  color: ${colors.interactive.primaryEnabled};
  display: flex;
  justify-content: space-between;
  max-height: 36px;
  padding: 7px 22px;

  svg {
    color: ${colors.interactive.primaryEnabled};
    height: 20px;
    width: 20px;
  }

  &:hover {
    background: ${colors.background.secondaryBackground};
    box-shadow: 0 0 0 1px ${colors.interactive.borderEnabledHoverGray} inset;
    color: ${colors.interactive.primaryHoverFocus};
  }

  &:focus {
    background: ${colors.background.secondaryBackground};
    box-shadow:
      0 0 0 3px ${colors.interactive.shadowFocusOrange},
      0 0 0 1px ${colors.interactive.borderFocusOrange} inset;
    color: ${colors.interactive.primaryHoverFocus};
  }

  &:active {
    background: ${colors.background.secondaryBackground};
    box-shadow: 0 0 0 1px ${colors.interactive.primaryHoverFocus} inset;
    color: ${colors.interactive.primaryHoverFocus};
  }

  &:disabled {
    background: ${colors.interactive.disabledLight};
    border: 1px solid ${colors.background.primaryBackground};
    color: ${colors.interactive.disabledDark};
    pointer-events: none;

    svg {
      color: ${colors.interactive.disabledDark};
    }
  }
`

/** *** Variant: Table **** */

const tableButtonLoadingStyles = css`
  background: ${colors.interactive.tableHoverFocus};
  box-shadow: 0 0 0 1px ${colors.interactive.primaryHoverFocus} inset;
  color: ${colors.copy.paragraphDark};
  cursor: not-allowed;
  pointer-events: none;

  svg {
    color: ${colors.copy.paragraphDark};
  }
`

const tableButtonStyles = css`
  align-items: center;
  background: ${colors.background.secondaryBackground};
  border: 1px solid ${colors.background.secondaryBackground};
  color: ${colors.interactive.primaryEnabled};
  display: flex;
  justify-content: space-between;
  max-height: 36px;
  padding: 7px 22px;

  svg {
    color: ${colors.interactive.primaryEnabled};
    height: 20px;
    width: 20px;
  }

  &:hover {
    background: ${colors.interactive.tableHoverFocus};
    box-shadow: 0 0 0 1px ${colors.interactive.borderEnabledHoverGray} inset;
    color: ${colors.interactive.primaryHoverFocus};
  }

  &:focus {
    background: ${colors.interactive.tableHoverFocus};
    box-shadow:
      0 0 0 3px ${colors.interactive.shadowFocusOrange},
      0 0 0 1px ${colors.interactive.borderFocusOrange} inset;
    color: ${colors.interactive.primaryHoverFocus};
  }

  &:active {
    background: ${colors.interactive.tableHoverFocus};
    box-shadow: 0 0 0 1px ${colors.interactive.primaryHoverFocus} inset;
    color: ${colors.interactive.primaryHoverFocus};
  }

  &:disabled {
    background: ${colors.interactive.disabledLight};
    border: 1px solid ${colors.background.primaryBackground};
    color: ${colors.interactive.disabledDark};
    pointer-events: none;

    svg {
      color: ${colors.interactive.disabledDark};
    }
  }
`

/** *** Base Button  **** */

const StyledButton = styled.button`
  cursor: pointer;
  display: flex;
  position: relative;

  &:hover {
    ${Tooltip} {
      display: block;
    }
  }
`
const flushTop = css`
  border-radius: 0 0 1000px 1000px;
`

const flushBottom = css`
  border-radius: 1000px 1000px 0 0;
`
const flushLeft = css`
  border-radius: 0 1000px 1000px 0;
`

const flushRight = css`
  border-radius: 1000px 0 0 1000px;
`

/** *** Chip Button **** */

export const ChipButton = styled(StyledButton)<{
  isLoading: boolean
  flush?: 'top' | 'bottom' | 'left' | 'right'
}>`
  ${({ isLoading, flush }) => css`
    ${chipButtonStyles}
    ${isLoading && chipButtonLoadingStyles}
    ${flush === 'top' && flushTop}
    ${flush === 'bottom' && flushBottom}
    ${flush === 'left' && flushLeft}
    ${flush === 'right' && flushRight}
    ${!flush && 'border-radius: calc(infinity * 1px);'}
  `}
`

/** *** Inline Button **** */

export const InlineButton = styled(StyledButton)<{
  isLoading: boolean
  size: InlineSize
}>`
  ${({ isLoading, size }) => css`
    ${inlineButtonStyles(size)}
    ${isLoading && inlineButtonLoadingStyles}
  `}
`

/** *** Primary Button **** */

export const PrimaryButton = styled(StyledButton)<{
  isLoading: boolean
  flush?: 'top' | 'bottom' | 'left' | 'right'
}>`
  ${({ isLoading, flush }) => css`
    ${primaryButtonStyles}
    ${isLoading && primaryButtonLoadingStyles}
    ${flush === 'top' && flushTop}
    ${flush === 'bottom' && flushBottom}
    ${flush === 'left' && flushLeft}
    ${flush === 'right' && flushRight}
    ${!flush && 'border-radius: calc(infinity * 1px);'}
  `}
`

/** *** Secondary Button **** */

export const SecondaryButton = styled(StyledButton)<{
  isLoading: boolean
  flush?: 'top' | 'bottom' | 'left' | 'right'
}>`
  ${({ isLoading, flush }) => css`
    ${secondaryButtonStyles}
    ${isLoading && secondaryButtonLoadingStyles}
    ${flush === 'top' && flushTop}
    ${flush === 'bottom' && flushBottom}
    ${flush === 'left' && flushLeft}
    ${flush === 'right' && flushRight}
    ${!flush && 'border-radius: calc(infinity * 1px);'}
  `}
`

/** *** Tertiary Button **** */

export const TertiaryButton = styled(StyledButton)<{
  isLoading: boolean
  flush?: 'top' | 'bottom' | 'left' | 'right'
}>`
  ${({ isLoading, flush }) => css`
    ${tertiaryButtonStyles}
    ${isLoading && tertiaryButtonLoadingStyles}
    ${flush === 'top' && flushTop}
    ${flush === 'bottom' && flushBottom}
    ${flush === 'left' && flushLeft}
    ${flush === 'right' && flushRight}
    ${!flush && 'border-radius: calc(infinity * 1px);'}
  `}
`

/** *** Form Button **** */

export const FormButton = styled(StyledButton)<{
  isLoading: boolean
  flush?: 'top' | 'bottom' | 'left' | 'right'
}>`
  ${({ isLoading, flush }) => css`
    ${formButtonStyles}
    ${isLoading && formButtonLoadingStyles}
    ${flush === 'top' && flushTop}
    ${flush === 'bottom' && flushBottom}
    ${flush === 'left' && flushLeft}
    ${flush === 'right' && flushRight}
    ${!flush && 'border-radius: calc(infinity * 1px);'}
  `}
`

/** *** Table Button **** */

export const TableButton = styled(StyledButton)<{
  isLoading: boolean
  flush?: 'top' | 'bottom' | 'left' | 'right'
}>`
  ${({ isLoading, flush }) => css`
    ${tableButtonStyles}
    ${isLoading && tableButtonLoadingStyles}
    ${flush === 'top' && flushTop}
    ${flush === 'bottom' && flushBottom}
    ${flush === 'left' && flushLeft}
    ${flush === 'right' && flushRight}
    ${!flush && 'border-radius: calc(infinity * 1px);'}
  `}
`
